import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Target, Heart, Award } from 'lucide-react';
import { siteData } from '../data/mock';

const iconMap = [Target, Heart, Award];

export default function Mission() {
  const { mission } = siteData;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const rotate = useTransform(scrollYProgress, [0, 1], [-8, 8]);

  return (
    <section id="mission" ref={ref} className="section relative overflow-hidden">
      {/* Background parallax image */}
      <motion.div
        style={{
          y: useTransform(scrollYProgress, [0, 1], [-80, 80]),
        }}
        className="absolute inset-0"
      >
        <img
          src={mission.image}
          alt="mission"
          className="w-full h-full object-cover opacity-20"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#05070f] via-[#05070f]/70 to-[#05070f]" />
      <div className="absolute inset-0 grid-lines opacity-10" />

      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="text-xs tracking-[0.4em] uppercase text-[#d5b56b] mb-6">
            — {mission.label}
          </div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#eef1f8]">
            {mission.title.split(' ').map((word, i) => (
              <span
                key={`${word}-${i}`}
                className={i === 2 ? 'italic text-gold' : ''}
              >
                {word}{' '}
              </span>
            ))}
          </h2>
          <p className="mt-8 text-lg leading-relaxed text-[#eef1f8]/70 max-w-2xl">
            {mission.description}
          </p>
        </motion.div>

        {/* Pillars */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {mission.pillars.map((p, i) => {
            const Icon = iconMap[i] || Award;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                whileHover={{ y: -8 }}
                className="glass rounded-2xl p-8 group"
              >
                <motion.div style={{ rotate }} className="w-14 h-14 rounded-2xl bg-[#d5b56b]/10 border border-[#d5b56b]/30 grid place-items-center mb-6 group-hover:bg-[#d5b56b] transition-colors duration-500">
                  <Icon size={22} className="text-[#d5b56b] group-hover:text-[#0a1230] transition-colors" />
                </motion.div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#d5b56b]/70 mb-2">0{i + 1}</div>
                <h3 className="font-serif text-3xl text-[#eef1f8] mb-3">{p.title}</h3>
                <p className="text-sm text-[#eef1f8]/60 leading-relaxed">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
